import { fetchData } from 'Utils/fetch-data';
import type { FetchResponseI } from 'types';
import type { SessionStatus, AttendanceStatus } from './sessions-api';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type SessionsReportGroupBy = 'course' | 'month' | 'instructor' | 'none';

export interface SessionsReportKpisI {
  totalSessions: number;
  completedSessions: number;
  completedPercentage: number;
  averageAttendancePercentage: number;
  totalAttendees: number;
  presentAttendees: number;
  totalHours: number;
  involvedCourses: number;
}

export interface SessionReportRowI {
  id: string;
  courseId: string;
  courseName: string;
  title: string;
  description: string | null;
  startDateTime: string;
  endDateTime: string;
  durationHours: number;
  instructorUserId: string | null;
  instructorName: string;
  location: string | null;
  status: SessionStatus;
  isManuallyCompleted: boolean;
  attendance: { present: number; total: number; percentage: number };
  /** Only present when query has `userId`. */
  userAttendance?: { status: AttendanceStatus | null; comment: string | null };
}

export interface SessionsReportGroupI {
  groupKey: string;
  groupLabel: string;
  groupMeta: { sessionsCount: number; totalHours: number; attendancePercentage: number };
  sessions: SessionReportRowI[];
}

export interface SessionsReportResponseI {
  kpis: SessionsReportKpisI;
  groups: SessionsReportGroupI[];
}

export interface SessionsReportFiltersI {
  page?: number;
  limit?: number;
  courseId?: string;
  instructorUserId?: string;
  userId?: string;
  status?: SessionStatus;
  startDate?: string;
  endDate?: string;
  search?: string;
  groupBy?: SessionsReportGroupBy;
}

/**
 * Course-level KPIs for the in-course "Sesiones" tab. Aggregated server-side
 * across ALL the course's sessions (not just the current page).
 */
export interface CourseSessionsSummaryI {
  totalSessions: number;
  completedSessions: number;
  scheduledSessions: number;
  averageAttendance: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// API client
// ─────────────────────────────────────────────────────────────────────────────

class SessionsReportApi {
  private readonly endpoint = '/reports/sessions';

  async fetchReport(filters: SessionsReportFiltersI = {}): Promise<FetchResponseI<SessionsReportResponseI>> {
    return fetchData<SessionsReportResponseI>(this.endpoint, {
      method: 'GET',
      params: filters,
      includePagination: true,
      includeJsonApiMeta: true,
    });
  }

  async exportReport(filters: SessionsReportFiltersI = {}): Promise<FetchResponseI<Blob>> {
    return fetchData<Blob>(`${this.endpoint}/export`, {
      method: 'GET',
      params: filters,
      responseType: 'blob',
    });
  }

  /**
   * Course-level KPIs for the in-course "Sesiones" tab.
   * Permission: `session.report.read` (same as the full report).
   */
  async getCourseSummary(courseId: string): Promise<FetchResponseI<CourseSessionsSummaryI>> {
    return fetchData<CourseSessionsSummaryI>(`${this.endpoint}/summary`, {
      method: 'GET',
      params: { courseId },
    });
  }
}

const sessionsReportApi = new SessionsReportApi();
export default sessionsReportApi;
